/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useMemo } from 'react';
import Head from 'next/head';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useLocale } from '@/hooks/useLocale';
import { getBaseSchemaUrl } from '@/utils/common';
import { useYextEntitySchema } from '@/features/locations/api/getYextEntitySchema';
import { metaTagKey } from '@/constants/seoMetaTags';
import { getWebsiteMeta } from '../../../../config/websiteMeta';
import { useLocationsSEOMetadata } from '@/features/locations/api/getLocationsSEOMetadata';
import type { LocationsSEOPageType } from '@/features/contentstack/interface';
import { useLocationsParams } from '../../hooks/useLocationsParams';
import { getCountryFromISO3166 } from '@/utils/getCountryFromLocale';
import { type YextDisplayCoordinate } from '../../interface/yext-types';
import { interpolateLabel } from '@/utils/labels/interpolateLabel';

type LocationsSeoTagsProps = {
  pageType: 'landing' | 'state' | 'city' | 'store';
  stateName?: string;
  cityName?: string;
  addressLine1?: string;
  storeNumber?: string;
  coordinates?: YextDisplayCoordinate;
  canonicalPath?: string;
};

export function LocationsSeoTags({
  pageType,
  stateName,
  cityName,
  addressLine1,
  storeNumber,
  coordinates,
  canonicalPath,
}: LocationsSeoTagsProps) {
  const locale = useLocale();
  const router = useLegacyRouter();
  const baseUrl = getBaseSchemaUrl(locale);
  const path = router.asPath.split('?')[0] ?? '';
  const absoluteUrl = `${baseUrl}${path}`;
  const canonicalUrl = `${baseUrl}${pageType === 'city' && canonicalPath ? canonicalPath : path}`;

  const country = getCountryFromISO3166(locale);

  const { data: entitySchema } = useYextEntitySchema(
    pageType === 'store' ? storeNumber : undefined,
  );

  const cmsPageType: LocationsSEOPageType =
    pageType === 'landing'
      ? 'LANDING'
      : pageType === 'state'
        ? 'STATE'
        : pageType === 'city'
          ? 'CITY'
          : 'STORE_DETAILS';

  const { state = '', city = '' } = useLocationsParams();

  const { data: seoMetadata } = useLocationsSEOMetadata({
    pageType: cmsPageType,
    state,
    city,
    enabled:
      cmsPageType === 'LANDING'
        ? true
        : cmsPageType === 'STATE'
          ? Boolean(state)
          : Boolean(state && city),
  });

  const logoUrl = 'https://www.autozone.com/images/az-logo-full.svg';

  const { title, description } = useMemo(() => {
    const replacementValues = {
      ...(stateName && { state: stateName }),
      ...(cityName && { city: cityName }),
      ...(addressLine1 && { address: addressLine1 }),
      ...(storeNumber && { storeNumber }),
    };

    if (seoMetadata?.seo) {
      const { page_title, meta_description } = seoMetadata.seo;

      if (page_title && meta_description) {
        return {
          title: interpolateLabel(page_title, replacementValues, true),
          description: interpolateLabel(meta_description, replacementValues, true),
        };
      }
    }

    return { title: null, description: null };
  }, [addressLine1, cityName, seoMetadata?.seo, stateName, storeNumber]);

  const localBusinessSchema = useMemo(() => {
    if (pageType !== 'store' || !entitySchema) {
      return '';
    }

    return JSON.stringify(entitySchema.response);
  }, [pageType, entitySchema]);

  return (
    <Head>
      {title ? <title key={metaTagKey('title')}>{title}</title> : null}
      {description ? (
        <meta key={metaTagKey('description')} name="description" content={description} />
      ) : null}

      <link key={metaTagKey('canonical')} rel="canonical" href={canonicalUrl} />

      {title ? <meta key={metaTagKey('og:title')} property="og:title" content={title} /> : null}
      <meta key={metaTagKey('og:url')} property="og:url" content={absoluteUrl} />
      <meta key={metaTagKey('og:site_name')} property="og:site_name" content="AutoZone.com" />
      <meta property="og:type" content="website" />
      <meta key={metaTagKey('og:image')} property="og:image" content={logoUrl} />
      {description ? <meta property="og:description" content={description} /> : null}

      <meta name="twitter:card" content="summary" />
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:site" content="@autozone" />
      <meta name="twitter:image" content={logoUrl} />

      {coordinates ? (
        <meta name="geo.position" content={`${coordinates.latitude},${coordinates.longitude}`} />
      ) : null}
      {stateName && cityName ? (
        <meta name="geo.placename" content={`${cityName},${stateName}`} />
      ) : null}
      {stateName ? <meta name="geo.region" content={`${country}-${stateName}`} /> : null}

      {pageType === 'store' && localBusinessSchema ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: localBusinessSchema }}
        />
      ) : null}

      <script
        key="websiteMeta"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: getWebsiteMeta(locale) }}
      />
    </Head>
  );
}
